import { useMemo, useState } from 'react'
import { Box, Typography, Grid, Card, CardActionArea, CardContent, Button, ToggleButtonGroup, ToggleButton, Divider, Paper, Chip, Stack, Snackbar, Alert, AppBar, Toolbar, IconButton, Tabs, Tab } from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import NotificationsIcon from '@mui/icons-material/Notifications'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import SearchIcon from '@mui/icons-material/Search'
import SettingsIcon from '@mui/icons-material/Settings'
import HomeIcon from '@mui/icons-material/Home'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import { useTranslation } from 'react-i18next'
import { THEME_PRESETS } from '../theme/presets'
import { useThemeContext } from '../contexts/ThemeContext'
import { useAuth } from '../contexts/AuthContext'
import { authApi } from '../api/auth'

export default function AppearancePage() {
  const { t } = useTranslation()
  const { mode, toggleMode, themeId, setThemeId } = useThemeContext()
  const { getEmpresaId } = useAuth()
  const empresaId = getEmpresaId()
  const [selected, setSelected] = useState(themeId || 'galatea-navy')
  const [saving, setSaving] = useState(false)
  const [openSnack, setOpenSnack] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const theme = useMemo(() => THEME_PRESETS.find(p => p.id === selected) || THEME_PRESETS[0], [selected])

  const handleSave = async () => {
    setSaving(true)
    try {
      setThemeId(selected)
      if (empresaId) {
        await authApi.updatePreferences(empresaId, { themeId: selected, mode })
      }
      setOpenSnack({ type: 'success', message: t('common.confirm') })
    } catch (e: any) {
      setOpenSnack({ type: 'error', message: 'Erro ao salvar preferências' })
    } finally {
      setSaving(false)
    }
  }

  const controlText = mode === 'dark' ? '#E2E8F0' : '#0B1220'
  const controlBorder = mode === 'dark' ? '#E2E8F0' : 'rgba(0,0,0,0.23)'
  const bg = mode === 'light' ? (selected === 'galatea-navy' ? '#E7F0FF' : '#F8FAFC') : '#0F172A'
  const paper = mode === 'light' ? '#FFFFFF' : '#1E293B'
  const text = mode === 'light' ? '#0B1220' : '#F1F5F9'
  const text2 = mode === 'light' ? '#475569' : '#CBD5E1'

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 3 }, width: '100%' }}>
      <Typography variant="h5" sx={{ mb: 1 }}>{t('config.appearanceTitle') || 'Aparência'}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('config.appearanceSubtitle') || 'Escolha o tema visual e o modo de cor.'}
      </Typography>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" gap={2} alignItems="center">
            <Chip label={t(`themePresets.${theme.id}.name`, { defaultValue: theme.name })} sx={{ bgcolor: theme.primary, color: '#fff' }} />
            <Typography variant="body2" color="text.secondary">{t(`themePresets.${theme.id}.recommendation`, { defaultValue: theme.recommendation })}</Typography>
          </Stack>
          <ToggleButtonGroup
            exclusive
            value={mode}
            onChange={(_, v) => { if (v && v !== mode) toggleMode() }}
            size="small"
          >
            <ToggleButton value="light" aria-label={t('theme.light')}><LightModeIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="dark" aria-label={t('theme.dark')}><DarkModeIcon fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Temas</Typography>
            <Grid container spacing={1.5}>
              {THEME_PRESETS.map(p => (
                <Grid item xs={6} sm={4} md={4} key={p.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderColor: selected === p.id ? p.primary : controlBorder,
                      bgcolor: mode === 'dark' ? (selected === p.id ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.03)') : undefined,
                      transition: 'background-color 120ms, border-color 120ms',
                      '&:hover': {
                        borderColor: selected === p.id ? p.primary : (mode === 'dark' ? '#F1F5F9' : 'rgba(0,0,0,0.4)'),
                        bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
                      },
                    }}
                  >
                    <CardActionArea onClick={() => { setSelected(p.id); setThemeId(p.id) }}>
                      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: controlText }}>
                        <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: p.primary }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: selected === p.id ? 600 : 500 }}>
                            {t(`themePresets.${p.id}.name`, { defaultValue: p.name })}
                          </Typography>
                          <Typography variant="caption" sx={{ color: mode === 'dark' ? '#CBD5E1' : 'text.secondary' }}>
                            {t(`themePresets.${p.id}.recommendation`, { defaultValue: p.recommendation })}
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
              >
                {t('common.save')}
              </Button>
              <Typography variant="caption" color="text.secondary">
                {empresaId ? 'Preferências serão salvas para esta empresa.' : 'Sem empresa selecionada: somente este navegador lembrará o tema.'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview</Typography>
        <Box sx={{ bgcolor: bg, color: text, p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ borderRadius: 1.5, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
            <AppBar position="static" sx={{ bgcolor: theme.primary }}>
              <Toolbar sx={{ minHeight: 56 }}>
                <IconButton edge="start" sx={{ color: '#fff' }}>
                  <MenuIcon />
                </IconButton>
                <Typography variant="subtitle1" sx={{ flexGrow: 1, color: '#fff' }}>Galatea</Typography>
                <IconButton sx={{ color: '#fff' }}><SearchIcon /></IconButton>
                <IconButton sx={{ color: '#fff' }}><NotificationsIcon /></IconButton>
                <IconButton sx={{ color: '#fff' }}><AccountCircleIcon /></IconButton>
              </Toolbar>
            </AppBar>
            <Box sx={{ p: 2, bgcolor: paper, color: text }}>
              <Tabs value="galatea" textColor="inherit" indicatorColor="secondary">
                <Tab value="galatea" label="Galatea" />
                <Tab value="bpa" label="BPA" />
              </Tabs>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" gap={1.5} sx={{ mb: 2 }}>
                <Button startIcon={<HomeIcon />} variant="contained" sx={{ bgcolor: theme.primary, '&:hover': { bgcolor: theme.primary } }}>Início</Button>
                <Button startIcon={<SettingsIcon />} variant="outlined">Configurações</Button>
              </Stack>
              <Paper sx={{ bgcolor: paper, color: text, p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Card de Exemplo</Typography>
                <Typography variant="body2" sx={{ color: text2, mb: 2, fontWeight: 500 }}>Texto secundário com contraste do modo {mode}.</Typography>
                <Stack direction="row" gap={1.5}>
                  <Button variant="contained" sx={{ bgcolor: theme.primary, '&:hover': { bgcolor: theme.primary } }}>Primário</Button>
                  <Button variant="outlined">Secundário</Button>
                </Stack>
              </Paper>
            </Box>
          </Box>
        </Box>
      </Paper>

      <Snackbar open={!!openSnack} autoHideDuration={3000} onClose={() => setOpenSnack(null)}>
        {openSnack ? <Alert severity={openSnack.type} onClose={() => setOpenSnack(null)}>{openSnack.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  )
}
